import { Component, Host, h, Element, Prop, Watch, JSX, State } from '@stencil/core';
import debounce from 'debounce';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { CmsImage, CmsLink, CmsVideo } from '../../types/global';
import { createAbsoutePathIfRelative } from '../../global/utility/tracking-helpers';

interface UiCmsStandardTeaserChip {
  text: string;
  color: 'white' | 'primary-200' | 'primary-300' | 'primary-500' | 'neutral-200' | 'neutral-300' | 'neutral-500';
}

interface UiCmsStandardTeaserIcon {
  iconPath: string;
  size: 'standard' | 'big';
  alignment: 'left' | 'center';
}

export interface UiCmsStandardTeaserMediaSettings {
  position: 'left' | 'right';
  size: 'xsmall' | 'small' | 'medium';
  removeSpacing?: boolean;
  divider?: boolean;
  verticalAlignment?: boolean;
}

export interface UiCmsStandardTeaserBody {
  headline?: string;
  text?: string;
  topic?: string;
  date?: string;
  author?: string;
  image?: CmsImage;
  mobileImage?: CmsImage;
  imageChip?: UiCmsStandardTeaserChip;
  flag?: UiCmsStandardTeaserChip;
  contentChips?: UiCmsStandardTeaserChip[];
  links: CmsLink[];
  linksPosition: 'left' | 'right';
  style?: 'white' | 'white border-neutral-400' | 'neutral-100' | 'primary' | 'primary-100';
  metadataPosition: 'top' | 'bottom';
  video?: CmsVideo;
  icon?: UiCmsStandardTeaserIcon;
  mediaSettings?: UiCmsStandardTeaserMediaSettings;
  removeSpacing?: boolean;
  orientation?: 'vertical' | 'horizontal';
}

const mediaSizeMapping = {
  xsmall: 25,
  small: 33.33,
  medium: 50,
};

@Component({
  tag: 'ui-cms-standard-teaser',
  styleUrl: 'ui-cms-standard-teaser.scss',
})
export class UiCmsStandardTeaser {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsStandardTeaserBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  /**
   * Boolean property used to vertically align link teasers in the same row. Used when grouping multiple teasers.
   */
  @Prop({ attribute: 'align-links' })
  alignLinks: boolean;

  /**
   * Property used to override orientation value passed from the body attribute. Used when grouping multiple teasers.
   */
  @Prop({ attribute: 'orientation-override' })
  orientationOverride: 'vertical' | 'horizontal';

  @State()
  orientation: 'vertical' | 'horizontal';

  private resizeObserver: ResizeObserver;

  @State()
  private imageSize: number;

  @State()
  private isMobile: boolean;

  private availableSizes = [240, 480, 640, 960, 1200, 2400];

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.addResizeObserver();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce((entries) => {
        this.orientation =
          entries[0].contentRect.width < 500 ? 'vertical' : (this.orientationOverride ?? this.body?.orientation);
        this.imageSize = this.availableSizes.find((size) => size > window.innerWidth);
        this.isMobile = window.outerWidth < 601;
      }, 100)
    );
    this.resizeObserver.observe(this.el);
  }

  private get hasMedia(): boolean {
    return Boolean(this.body.image || this.body.video || this.body.icon);
  }

  private get isInverted(): boolean {
    return this.body.style === 'primary';
  }

  private get contentMediaSizeDistributions() {
    if (this.orientation === 'vertical') {
      return undefined;
    }

    const media = !this.hasMedia ? 0 : mediaSizeMapping[this.body.mediaSettings.size] || 50;
    const content = !this.hasMedia ? 100 : 100 - mediaSizeMapping[this.body.mediaSettings.size] || 50;

    return {
      media,
      content,
    };
  }

  private get copyright() {
    const imageCopyright = this.isMobile ? this.body.mobileImage?.copyright : this.body.image?.copyright;
    const copyrightText = imageCopyright || this.body.video?.copyright;

    return copyrightText ? `'© ${copyrightText}'` : undefined;
  }

  private get imageAlt() {
    return this.isMobile ? this.body.mobileImage?.alt : this.body.image?.alt;
  }

  private handleTracking(link: CmsLink): void {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Teaser',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Teaser',
        elementLabel: link.title,
        target: createAbsoutePathIfRelative(link.src),
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  private renderMetadata(): JSX.Element {
    return (
      <span
        class={{
          'sd-meta sd-meta--light sd-meta--size-sm': true,
          'sd-meta--inverted': this.isInverted,
          'standard-teaser__bottom-meta-spacing': this.body.metadataPosition === 'bottom',
        }}
      >
        {this.body.date && (
          <span class={{ 'sd-meta--pipe': !!(this.body.author || this.body.topic) }}>
            {new Date(this.body.date).toLocaleDateString(document.documentElement.lang)}
          </span>
        )}
        {this.body.author && <span class={{ 'sd-meta--pipe': !!this.body.topic }}>{this.body.author}</span>}
        {this.body.topic && <span>{this.body.topic}</span>}
      </span>
    );
  }

  private renderSdTeaser(): JSX.Element {
    return (
      <sd-teaser
        variant={this.body.style}
        /** Breakpoint where the teaser switches from vertical to horizontal. 0 is always horizontal, 9999 is always vertical */
        breakpoint={this.orientation === 'vertical' ? 9999 : 0}
        style={
          this.orientation === 'horizontal' && {
            '--distribution-media': `${this.contentMediaSizeDistributions.media}%`,
            '--distribution-content': `${this.contentMediaSizeDistributions.content}%`,
          }
        }
        class={{
          'standard-teaser': true,
          [`standard-teaser--media-position-${this.body.mediaSettings?.position ?? 'left'}`]:
            this.orientation === 'horizontal' && this.hasMedia,
          [`standard-teaser--${this.orientation}-variant-spacing`]: !this.body.removeSpacing,
          'standard-teaser--meta-position-bottom': this.body.metadataPosition === 'bottom',
          'standard-teaser--no-media-spacing':
            this.body.mediaSettings?.removeSpacing && this.orientation === 'horizontal',
          [`standard-teaser--no-spacing-${this.orientation}`]: this.body.removeSpacing,
          'standard-teaser--vertical-alignment': this.body.mediaSettings?.verticalAlignment,
          'standard-teaser--exclude-content-main': !this.body.links?.length && !this.body.contentChips?.length,
          'standard-teaser--align-links-bottom': this.alignLinks,
          'standard-teaser--exclude-meta': !this.body.topic && !this.body.author && !this.body.date,
        }}
      >
        {this.hasMedia && (
          <figure
            slot="media"
            class={{ 'standard-teaser__media': true, 'sd-copyright': !!this.copyright }}
            style={{ '--copyright': this.copyright }}
          >
            {this.body.image && (
              <ui-cms-image
                src={this.body.image.src}
                mobileSrc={this.body.mobileImage?.src}
                alt={this.imageAlt}
                objectPosition={this.body.image.objectPosition}
                size={this.imageSize}
              ></ui-cms-image>
            )}
            {this.body.video && (
              <ui-cms-video
                videoId={this.body.video.videoId}
                loop={this.body.video.loop}
                muted={this.body.video.muted}
              ></ui-cms-video>
            )}
            {this.body.icon && (
              <div
                class={{
                  'standard-teaser__media__icon': true,
                  'standard-teaser__media__icon--size-big': this.body.icon.size === 'big',
                  'standard-teaser__media__icon--align-left': this.body.icon.alignment === 'left',
                }}
              >
                <sd-icon
                  name={this.body.icon.iconPath}
                  library={window.ui.cmsState.iconLibrary}
                  color={this.isInverted ? 'white' : 'primary'}
                ></sd-icon>
              </div>
            )}
            {this.body.imageChip && (
              <span class={`sd-chip sd-chip--${this.body.imageChip.color} standard-teaser__media__chip`}>
                {this.body.imageChip.text}
              </span>
            )}{' '}
            {this.body.flag && (
              <span class={`sd-flag sd-flag--${this.body.flag.color} standard-teaser__media__flag`}>
                {this.body.flag.text}
              </span>
            )}
          </figure>
        )}
        {(!this.body.metadataPosition || this.body.metadataPosition === 'top') && (
          <span slot="meta">{this.renderMetadata()}</span>
        )}
        <div slot="headline">
          <h4 class="standard-teaser__headline">{this.body.headline}</h4>
          <p class={{ 'sd-paragraph': true, 'sd-paragraph--inverted': this.isInverted }} innerHTML={this.body.text}></p>
        </div>
        {this.body.metadataPosition === 'bottom' && this.renderMetadata()}
        {this.body.contentChips?.length > 0 && (
          <div class="standard-teaser__content-chips">
            {this.body.contentChips?.map((chip) => <span class={`sd-chip sd-chip--${chip.color}`}>{chip.text}</span>)}
          </div>
        )}
        {this.body.links?.length && (
          <div
            class={{
              'standard-teaser__links': true,
              'standard-teaser__links--align-right': this.body.linksPosition === 'right',
            }}
          >
            {this.body.links?.map((link) => (
              <ui-cms-link
                body={link}
                inverted={this.isInverted}
                disableTracking
                onClick={() => this.body.links.length !== 1 && this.handleTracking(link)}
              ></ui-cms-link>
            ))}
          </div>
        )}
      </sd-teaser>
    );
  }

  render() {
    return (
      <Host>
        {this.body.links?.length === 1 ? (
          <a
            href={this.body.links[0].src}
            target={this.body.links[0].target}
            aria-label={this.body.links[0].ariaLabel}
            onClick={() => this.handleTracking(this.body.links[0])}
          >
            {this.renderSdTeaser()}
          </a>
        ) : (
          this.renderSdTeaser()
        )}
        {this.body.mediaSettings?.divider && <sd-divider orientation="horizontal"></sd-divider>}
      </Host>
    );
  }
}
